import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import StudentsTable from "../../components/Dashboard/Students/StudentsTable";
import Spinner from "../../components/Shared/Spinner";
import { getStudents } from "../../api/Students/GetAllStudents";
import Input from "../../components/Shared/Input";
import Selectbox, { option } from "../../components/Shared/SelectBox";

const statusOptions: option[] = [
  { name: "approved" },
  { name: "pending" },
];

const Students = () => {
  const [page, setPage] = useState(1);
  const [limit,] = useState(10);
  const [jumpPage, setJumpPage] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState<option | null>(null);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [debouncedFirstName, setDebouncedFirstName] = useState(firstName);
  const [debouncedPhone, setDebouncedPhone] = useState(phone);
  const [debouncedEmail, setDebouncedEmail] = useState(email);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFirstName(firstName);
      setDebouncedPhone(phone);
      setDebouncedEmail(email);
    }, 2000);

    return () => {
      clearTimeout(handler);
    };
  }, [firstName, phone, email]);

  const { data: StudentsResponse, isLoading: isStudentsLoading } = useQuery({
    queryFn: () => getStudents({ page, limit, firstName: debouncedFirstName, status: status?.name, email: debouncedEmail, phone: debouncedPhone }),
    queryKey: ["students", page, limit, debouncedFirstName, status, debouncedPhone, debouncedEmail],
    staleTime: 0,
    refetchInterval: 0,
  });

  const handleNextPage = () => setPage((prev) => prev + 1);
  const handlePreviousPage = () => setPage((prev) => Math.max(prev - 1, 1));
  const handleJumpPage = () => setPage(jumpPage);
  const handleClearStatus = () => setStatus(null);

  const students = StudentsResponse?.data?.data || [];
  const metaData = StudentsResponse?.data?.metaData;


  if (isStudentsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <h1 className="text-burntBlack font-bold text-[32px] mb-7">Students</h1>
      <div className='p-8 rounded-xl bg-white border-black/15 shadow'>
        <h1 className='font-bold text-2xl mb-8'>Students Details</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Input
            name="FirstName"
            type="text"
            className="md:!space-y-0"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First Name"
            inputStyles="!rounded !h-[50px]"
          />
          <div className="relative">
            <Selectbox
              selectOptions={statusOptions}
              selected={status}
              setSelected={setStatus}
              placeholder="Select Status"
              className="!rounded"
              titleStyles="!mb-0"
            />
            {status && (
              <button
                onClick={handleClearStatus}
                className="absolute top-1/2 right-4 transform -translate-y-1/2 text-red-500 hover:text-red-700"
              >
                Clear
              </button>
            )}
          </div>
          <Input
            name="Phone"
            type="text"
            className="md:!space-y-0"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone"
            inputStyles="!rounded !h-[50px]"
          />
          <Input
            name="email"
            type="email"
            className="md:!space-y-0"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            inputStyles="!rounded !h-[50px]"
          />
        </div>

        <StudentsTable data={students} />

        <div className="flex justify-between items-center mt-4">
          <button
            onClick={handlePreviousPage}
            disabled={page === 1}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Previous
          </button>
          <span>
            Page {page} of {metaData?.totalPages || 1}
          </span>
          <button
            onClick={handleNextPage}
            disabled={!metaData || page >= metaData.totalPages}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-gray-400"
          >
            Next
          </button>
        </div>

        <div className="flex justify-center items-center mt-4">
          <Input
            name="Page"
            type="number"
            value={jumpPage.toString()}
            className="md:!space-y-0"
            inputStyles="!h-10 !rounded"
            onChange={(e) => setJumpPage(Number(e.target.value))}
          />
          <button
            onClick={handleJumpPage}
            className="bg-blue-500 text-white px-4 h-10 rounded disabled:bg-gray-400"
          >
            Jump to Page
          </button>
        </div>
      </div>
    </div>
  );
}

export default Students;
